import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { AdminBackButton } from "../console/AdminBackButton";
import api from "../console/api";
import { adminLogout } from "./adminSession";
import {
  Users, FilePlus2, Hourglass, PackageOpen, Truck, PackageCheck, Package, ScrollText,
  DollarSign, AlertOctagon, Gift, FileEdit, FlaskConical, Key,
  Archive, LogOut, Menu, X, Flag, Sparkles, Heart, PartyPopper, Upload, Store,
  Crown, Scale, Flower2, Mail, Landmark, ChartNoAxesCombined, ClipboardCheck, ListChecks, CalendarDays, Send, Sprout, Ticket, Printer,
} from "lucide-react";

/**
 * /admin Admin Panel layout , Feb 2026 reset rebuild.
 *
 * 14 simple tabs (the only ones Kayla wants kept) plus the temporary
 * Historic Migration tab. Visual styling matches the rebuilt Heritage
 * stained-glass + parchment system used on /console and HQ.
 */
const SEAL = "/photos/brand/heritage-seal.png";
const AUBERGINE = "#3E1F46";
const AUBERGINE_DEEP = "#2C1531";
const GOLD = "#C9A268";
const CREAM = "#FBF6E9";
const PARCH = "#F4ECD8";

const L = (to, label, Icon, testid, extra = {}) => ({ to, label, Icon, testid, ...extra });

/** Six main destinations. Every legacy route stays reachable as a sub-item (nothing deleted). */
export const PRIMARY = [
  { key: "home", label: "Today", Icon: Sparkles, to: "/admin", end: true, testid: "admin-nav-dashboard", items: [] },
  { key: "customers", label: "Customers", Icon: Users, to: "/admin/customers", testid: "admin-nav-customers", items: [
    L("/admin/customers",             "Customer Database",      Users,          "admin-nav-customers-db"),
    L("/admin/messages",              "Communications",         Mail,           "admin-nav-messages"),
    L("/admin/customers/priority",    "Priority / Needs review", Flag,          "admin-nav-priority"),
    L("/admin/cutting-requests",      "Cutting Requests ♡",     Heart,          "admin-nav-cutting-requests"),
    L("/admin/customer-audit",        "Customer Audit",         ClipboardCheck, "admin-nav-customer-audit"),
    L("/admin/orders/complaints",     "Complaints / Refunds",   AlertOctagon,   "admin-nav-complaints"),
    L("/admin/orders/historical",     "Historical Customers",   Archive,        "admin-nav-historical-customers"),
    L("/admin/customers/wishlist-heatmap", "Wishlist Heatmap",  Heart,          "admin-nav-wishlist-heatmap"),
    L("/admin/mass-email",            "Mass Email (Gmail draft)", Send,         "admin-nav-mass-email"),
  ]},
  { key: "orders", label: "Orders", Icon: PackageOpen, to: "/admin/orders", testid: "admin-nav-orders", items: [
    L("/admin/orders",                "All orders (cards)",     PackageOpen,    "admin-nav-all-orders"),
    L("/admin/orders?filter=attention","Needs attention",       Hourglass,      "admin-nav-awaiting-payment"),
    L("/admin/orders?filter=to_pack", "To pack",                PackageCheck,   "admin-nav-packing"),
    L("/admin/orders?filter=sent",    "Sent",                   Truck,          "admin-nav-to-ship"),
    L("/admin/orders/legacy/packing", "Legacy packing list",    ScrollText,     "admin-nav-legacy-packing"),
    L("/admin/pull-list",             "Pull List",              ListChecks,     "admin-nav-pull-list"),
    L("/admin/holds",                 "Holds to review",        AlertOctagon,   "admin-nav-holds"),
    L("/admin/print",                 "Documents & Print",      Printer,        "admin-nav-print-sub"),
    L("/admin/orders/create",         "Create Order",           FilePlus2,      "admin-nav-create"),
    L("/admin/orders/archived",       "Archived Orders",        Archive,        "admin-nav-archived-orders"),
    L("/admin/orders/completed-historical", "Completed Historical", Archive,    "admin-nav-completed-historical"),
    L("/admin/bulk-import",           "Bulk Import Orders",     Upload,         "admin-nav-bulk-import"),
  ]},
  { key: "print", label: "Documents & Print", Icon: Printer, to: "/admin/print", testid: "admin-nav-print", items: [] },
  { key: "delivery", label: "Delivery", Icon: Truck, to: "/admin/delivery", testid: "admin-nav-delivery", items: [
    L("/admin/delivery",              "Local delivery queue",   Truck,          "admin-nav-delivery-queue"),
    L("/console/delivery-routes",     "Saved routes & maps",    Truck,          "admin-nav-delivery-routes"),
  ]},
  { key: "varieties", label: "Garden & Stock", Icon: Flower2, to: "/admin/catalog", testid: "admin-nav-varieties", items: [
    L("/admin/catalog",               "Catalogue",              Sparkles,       "admin-nav-catalog"),
    L("/admin/tuber-audit",           "Tuber Audit",            Package,        "admin-nav-tuber-audit"),
    L("/admin/available-to-sell",     "Available To Sell",      Sprout,         "admin-nav-available-to-sell"),
    L("/admin/store-products",        "Store Products",         Store,          "admin-nav-store-products"),
    L("/admin/label-maker",           "Label Maker",            Sparkles,       "admin-nav-label-maker"),
    L("/admin/fairsale",              "Fairitage Sale",         Scale,          "admin-nav-fairsale"),
  ]},
  { key: "more", label: "More", Icon: Menu, testid: "admin-nav-more", groups: [
    { group: "Stock Audit", items: [
      L("/admin/stock-reconcile",     "Stock Reconciliation",   Scale,          "admin-nav-stock-reconcile"),
    ]},
    { group: "Money", items: [
      L("/admin/finance",             "Finance HQ",             ChartNoAxesCombined, "admin-nav-finance"),
      L("/admin/payments",            "Money owed",             DollarSign,     "admin-nav-payments"),
      L("/admin/bank-auditor-queue",  "Bank reconciliation",    Landmark,       "admin-nav-bank-auditor-queue"),
      L("/admin/vouchers",            "Vouchers & Credits",     Gift,           "admin-nav-vouchers"),
    ]},
    { group: "Bookings & Events", items: [
      L("/visit/calendar",            "Public Summer Calendar",  CalendarDays,   "admin-nav-calendar"),
      L("/admin/private-bookings",    "Private Bookings",       CalendarDays,   "admin-nav-private-bookings"),
      L("/admin/open-days",           "Open Days & Tickets",    Ticket,         "admin-nav-open-days"),
      L("/admin/visit",               "Promotions & Giveaways", PartyPopper,    "admin-nav-visit"),
    ]},
    { group: "Website & Images", items: [
      L("/admin/site-builder",        "Site Builder",           Store,          "admin-nav-site-builder"),
      L("/admin/content",             "Website Content",        FileEdit,       "admin-nav-content"),
    ]},
    { group: "Settings", items: [
      L("/admin/bumface",             "Staff Workboard",        Hourglass,      "admin-nav-bumface"),
      L("/admin/staff",               "Heritage Staff & access", Users,         "admin-nav-staff"),
      L("/admin/seedlings",           "Breeders Club Seedlings", Flower2,       "admin-nav-seedlings"),
      L("/admin/test-accounts",       "HQ impersonation (audited)", FlaskConical, "admin-nav-test"),
      L("/admin/reset-password",      "Reset Password",         Key,            "admin-nav-reset-password"),
    ]},
  ]},
];

/** Flat grouped view (used by Staff permissions). */
export const NAV = PRIMARY.flatMap((p) => p.groups ? p.groups : [{ group: p.label, items: p.items.length ? p.items : [L(p.to, p.label, p.Icon, p.testid, { end: p.end })] }]);

export default function AdminPanelLayout() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [allowedTabs, setAllowedTabs] = useState(null);
  const [noteAlert, setNoteAlert] = useState(0);
  const [commsAlert, setCommsAlert] = useState(0);

  useEffect(() => {
    const poll = () => {
      const since = localStorage.getItem("heritage-bumface-seen") || "";
      const me = localStorage.getItem("heritage-staff-me") || "";
      api.get("/console/hours/tasks/activity", { params: { since, exclude_author: me } })
        .then((r) => setNoteAlert(r.data.new_messages || 0))
        .catch(() => {});
      api.get("/admin/customer-messages/unread-count")
        .then((r) => setCommsAlert(r.data.unread || 0))
        .catch(() => {});
    };
    poll();
    const iv = setInterval(poll, 60000);
    const clear = () => setNoteAlert(0);
    window.addEventListener("bumface-seen", clear);
    window.addEventListener("comms-read", poll);
    return () => {
      clearInterval(iv);
      window.removeEventListener("bumface-seen", clear);
      window.removeEventListener("comms-read", poll);
    };
  }, []);

  useEffect(() => {
    const email = (localStorage.getItem("heritage-admin-email") || "").toLowerCase();
    if (!email) return;
    api.get("/console/staff").then((r) => {
      const me = (r.data.staff || []).find((s) => (s.email || "").toLowerCase() === email);
      if (me && Array.isArray(me.tabs) && me.tabs.length) {
        setAllowedTabs(new Set(["/admin", ...me.tabs]));
      }
    }).catch(() => {});
  }, []);

  const location = useLocation();
  const filterItems = (items) => allowedTabs ? items.filter((i) => allowedTabs.has(i.to)) : items;
  const primary = PRIMARY.map((p) => p.groups
    ? { ...p, groups: p.groups.map((g) => ({ ...g, items: filterItems(g.items) })).filter((g) => g.items.length) }
    : { ...p, items: filterItems(p.items) })
    .filter((p) => p.key === "home" || (p.groups ? p.groups.length : (p.items.length || !allowedTabs || allowedTabs.has(p.to))));
  const sectionFor = (path) => PRIMARY.find((p) => p.groups ? p.groups.some((g) => g.items.some((i) => path.startsWith(i.to))) : (p.items.some((i) => path.startsWith(i.to)) || (p.to !== "/admin" && path.startsWith(p.to))))?.key || "home";
  const [openKey, setOpenKey] = useState(() => sectionFor(location.pathname));
  useEffect(() => {
    setOpenKey(sectionFor(location.pathname));
    if (/^\/admin\/(orders|customers|pull-list|delivery|private-bookings|open-days|catalog|tuber-audit)/.test(location.pathname)) {
      const item = PRIMARY.flatMap((p) => p.groups ? p.groups.flatMap((g) => g.items) : p.items).find((i) => location.pathname.startsWith(i.to));
      localStorage.setItem("heritage-admin-last-list", JSON.stringify({ to: location.pathname + location.search, label: item?.label || location.pathname }));
    }
  }, [location.pathname, location.search]);

  const logout = async () => {
    await adminLogout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen" data-testid="admin-panel-layout" style={{ backgroundColor: CREAM, color: "#2A1F1A" }}>
      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 py-3 border-b" style={{ backgroundColor: AUBERGINE, borderColor: AUBERGINE_DEEP }}>
        <button type="button" onClick={() => setOpen(true)} data-testid="admin-mobile-menu" className="inline-flex items-center gap-2 text-white">
          <Menu className="w-5 h-5" />
          <span className="text-[11px] tracking-[0.3em] uppercase font-semibold">Admin</span>
        </button>
        <div className="text-right leading-tight text-white">
          <div className="text-[9px] tracking-[0.35em] uppercase opacity-80">Heritage Dahlias</div>
          <div className="font-serif text-base">Admin Panel</div>
        </div>
      </div>

      <div className="lg:grid lg:grid-cols-[260px_1fr] min-h-screen">
        {open && <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" />}

        <aside
          className={"flex flex-col fixed lg:static inset-y-0 left-0 z-50 w-[260px] lg:w-auto transform transition-transform duration-200 ease-out lg:transform-none " + (open ? "translate-x-0" : "-translate-x-full lg:translate-x-0")}
          data-testid="admin-sidebar"
          style={{ backgroundColor: AUBERGINE, backgroundImage: `linear-gradient(180deg, ${AUBERGINE} 0%, ${AUBERGINE_DEEP} 100%)`, color: PARCH }}
        >
          <div className="px-5 py-5 border-b flex items-center gap-3" style={{ borderColor: "rgba(244,236,216,0.12)" }}>
            <div className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center" style={{ backgroundColor: CREAM, boxShadow: `0 0 0 2px ${GOLD}` }}>
              <img src={SEAL} alt="Heritage Dahlias" className="w-9 h-9" />
            </div>
            <div className="leading-tight">
              <div className="text-[9px] tracking-[0.4em] uppercase opacity-70 font-semibold">Heritage Dahlias</div>
              <div className="font-serif text-xl" style={{ color: CREAM, letterSpacing: "0.02em" }}>Admin Panel</div>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="lg:hidden ml-auto text-[#F4ECD8]/70 hover:text-[#F4ECD8]" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto py-3 px-2" data-testid="admin-primary-nav">
            {primary.map((p) => {
              const isOpen = openKey === p.key;
              const Badge = ({ n, id }) => n > 0 ? <span data-testid={id} className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{n > 9 ? "9+" : n}</span> : null;
              return (
                <div key={p.key} className="mb-1">
                  {p.to && !p.groups ? (
                    <NavLink to={p.to} end={p.end} data-testid={p.testid} onClick={() => { setOpenKey(p.key); if (!p.items.length) setOpen(false); }}
                      className={({ isActive }) => "mx-1 px-3 py-2.5 flex items-center gap-2.5 rounded-sm text-[14px] font-serif transition " + (isActive || isOpen ? "bg-white/95 text-[#3E1F46] font-semibold shadow-sm" : "text-[#F4ECD8]/90 hover:bg-white/10 hover:text-white")}>
                      <p.Icon className="w-4 h-4 opacity-80 shrink-0" /><span>{p.label}</span>
                      {p.key === "customers" && <Badge n={commsAlert} id="comms-unread-badge" />}
                    </NavLink>
                  ) : (
                    <button type="button" data-testid={p.testid} onClick={() => setOpenKey(isOpen ? "" : p.key)}
                      className={"mx-1 w-[calc(100%-8px)] px-3 py-2.5 flex items-center gap-2.5 rounded-sm text-[14px] font-serif transition " + (isOpen ? "bg-white/95 text-[#3E1F46] font-semibold shadow-sm" : "text-[#F4ECD8]/90 hover:bg-white/10 hover:text-white")}>
                      <p.Icon className="w-4 h-4 opacity-80 shrink-0" /><span>{p.label}</span>
                      <Badge n={noteAlert} id="bumface-note-badge" />
                    </button>
                  )}
                  {isOpen && (p.groups || (p.items.length > 1 ? [{ group: null, items: p.items }] : [])).map((g, gi) => (
                    <div key={g.group || gi} className="ml-4 mt-1 mb-2 border-l pl-2" style={{ borderColor: "rgba(244,236,216,0.2)" }} data-testid={`admin-subnav-${p.key}`}>
                      {g.group && <div className="px-2 mt-2 mb-0.5 text-[9px] tracking-[0.35em] uppercase font-semibold" style={{ color: GOLD, opacity: 0.8 }}>{g.group}</div>}
                      {g.items.map((it) => (
                        <NavLink key={it.to} to={it.to} end={it.end} data-testid={it.testid} onClick={() => setOpen(false)}
                          className={({ isActive }) => "px-2 py-1.5 my-0.5 flex items-center gap-2 rounded-sm text-[12.5px] font-serif transition " + (isActive ? "bg-white/20 text-white font-semibold" : "text-[#F4ECD8]/75 hover:bg-white/10 hover:text-white")}>
                          <it.Icon className="w-3.5 h-3.5 opacity-70 shrink-0" /><span className="truncate">{it.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  ))}
                </div>
              );
            })}
          </nav>

          <div className="p-3 border-t space-y-2" style={{ borderColor: "rgba(244,236,216,0.12)" }}>
            <button
              type="button"
              onClick={() => navigate("/console")}
              data-testid="admin-open-management"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm text-[10px] tracking-[0.25em] uppercase font-semibold transition border"
              style={{ borderColor: GOLD, color: GOLD }}
            >
              Open Management Portal →
            </button>
            <button type="button" onClick={logout} data-testid="admin-logout" className="w-full text-[11px] tracking-[0.2em] uppercase inline-flex items-center justify-center gap-2 opacity-70 hover:opacity-100" style={{ color: CREAM }}>
              <LogOut className="w-3.5 h-3.5" /> Log out
            </button>
          </div>
        </aside>

        <main className="p-4 sm:p-6 lg:p-8 max-w-full w-full min-w-0 overflow-x-hidden">
          <AdminBackButton />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
